import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Box, Button, Stack, TextField, Link, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link as RouterLink } from 'react-router-dom';
import { Turnstile } from '@marsidev/react-turnstile';
import PasswordField from './PasswordField';

export const signupSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters long')
    .max(128, 'Password must be less than 128 characters')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(
      /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, //   /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
      'Password must contain at least one special character'
    )
    .test('not-common', 'Password is too common', function (value) {
      if (!value) return true; // Let required() handle empty values
      const commonPasswords = [
        'password',
        'password123',
        '123456',
        '123456789',
        'qwerty',
        'abc123',
        'password1',
        '12345678',
        '111111',
        '123123',
      ];
      return !commonPasswords.includes(value.toLowerCase());
    })
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
});

type SignupFormData = yup.InferType<typeof signupSchema>;

interface SignupFormProps {
  onSubmit: (data: SignupFormData & { turnstileToken?: string }) => Promise<void>;
  isLoading?: boolean;
  invitationToken?: string;
  submitButtonText?: string;
}

export interface SignupFormRef {
  reset: () => void;
}

const SignupForm = forwardRef<SignupFormRef, SignupFormProps>(
  ({ onSubmit, invitationToken, isLoading, submitButtonText }, ref) => {
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState<string>('');

    const {
      register,
      handleSubmit,
      setValue,
      reset,
      formState: { errors, isSubmitting },
    } = useForm<SignupFormData>({
      resolver: yupResolver(signupSchema),
    });

    useImperativeHandle(ref, () => ({
      reset: () => {
        reset();
        setIsPasswordValid(false);
        setTurnstileToken('');
      },
    }));

    const handlePasswordChange = (newPassword: string) => {
      setValue('password', newPassword, { shouldValidate: true });
    };

    const handleConfirmPasswordChange = (newConfirmPassword: string) => {
      setValue('confirmPassword', newConfirmPassword, { shouldValidate: true });
    };

    const handlePasswordValidation = (isValid: boolean) => {
      setIsPasswordValid(isValid);
    };

    const handleFormSubmit = async (data: SignupFormData) => {
      const submitData = {
        ...data,
        ...(invitationToken && { invitationToken }),
        ...(turnstileToken && { turnstileToken }),
      };
      await onSubmit(submitData);
    };

    return (
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="First Name"
              {...register('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />

            <TextField
              fullWidth
              label="Last Name"
              {...register('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Stack>

          <TextField
            fullWidth
            label="Email"
            type="email"
            {...register('email')}
            error={!!errors.email}
            helperText={errors.email?.message}
          />

          <PasswordField
            onPasswordChange={handlePasswordChange}
            onConfirmPasswordChange={handleConfirmPasswordChange}
            onValidationChange={handlePasswordValidation}
            showConfirmField={true}
            showValidation={true}
            disabled={isSubmitting}
            margin="none"
          />

          {/* Show form validation errors if they exist and the custom validation passed */}
          {(errors.password || errors.confirmPassword) && isPasswordValid && (
            <Box sx={{ mt: 1 }}>
              {errors.password && (
                <Typography variant="body2" color="error.main" sx={{ mb: 0.5 }}>
                  {errors.password.message}
                </Typography>
              )}
              {errors.confirmPassword && (
                <Typography variant="body2" color="error.main">
                  {errors.confirmPassword.message}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <Turnstile
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken('')}
              onExpire={() => setTurnstileToken('')}
              options={{ theme: 'light' }}
            />
          </Box>

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting || !isPasswordValid || !turnstileToken || isLoading}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? 'Creating account...' : submitButtonText}
          </Button>
        </Stack>

        <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
          Already have an account?{' '}
          <Link
            component={RouterLink}
            to={invitationToken ? `/login?invitationToken=${invitationToken}` : '/login'}
            underline="none"
          >
            Log in
          </Link>
        </Typography>
      </Box>
    );
  }
);

export default SignupForm;
